import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { analyseSections, emptySectionModel } from '@nopain-newsletter/core/sections';
import type { SectionEditModel } from '@nopain-newsletter/core/sections';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { CopyImagesPanel } from '../components/html-template/CopyImagesPanel';
import { useHtmlCheck } from '../components/html-template/useHtmlCheck';
import { useRehostImages } from '../components/html-template/useRehostImages';
import { SectionEditorScreen } from '../components/section-editor/SectionEditorScreen';
import type {
  SectionSaveFailure,
  SectionSaveResult,
} from '../components/section-editor/useSectionEditor';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ApiError,
  type Campaign,
  type SectionSaveResponse,
  type StoredSectionEdits,
  type TemplateDetailResponse,
} from '../types/api';

type Kind = 'template' | 'campaign';

/** What the editor needs to know about the mail, whether it is a template or a campaign. */
interface Subject {
  name: string;
  html: string;
  isHtml: boolean;
  sections: StoredSectionEdits | null;
  /** Why it cannot be changed, or null. */
  locked: string | null;
}

const PATHS = {
  template: {
    api: 'templates',
    page: 'skabeloner',
    queryKey: 'template',
    back: 'Back to the template',
  },
  campaign: {
    api: 'campaigns',
    page: 'kampagner',
    queryKey: 'campaign',
    back: 'Back to the campaign',
  },
} as const;

/** Turns a failed save into what the editor shows. The server's own message is used when it sent one. */
export function sectionSaveFailure(err: unknown): SectionSaveFailure {
  if (!(err instanceof ApiError)) {
    return { message: 'The section edits could not be saved. Try again shortly.' };
  }
  const body = err.body;
  const errors = Array.isArray(body.errors)
    ? body.errors
        .map(
          (e) =>
            (e as { message?: unknown; code?: unknown }).message ?? (e as { code?: unknown }).code,
        )
        .filter((m): m is string => typeof m === 'string')
    : [];
  if (body.error === 'sections_original_changed') {
    return {
      stale: true,
      message:
        body.message ??
        'The HTML of this mail was changed somewhere else, so these section edits no longer fit. Reload the mail.',
    };
  }
  if (body.error === 'template_invalid') {
    return { message: 'The server refused the mail with these changes:', errors };
  }
  if (err.status === 403) {
    return { message: 'Your role cannot change this mail. Ask an editor or an admin.' };
  }
  return {
    message: body.message ?? 'The section edits could not be saved. Try again shortly.',
    errors,
  };
}

/**
 * Before the first section edit: images that are not on our own media
 * domain. Copying them rewrites the HTML, and with it the ids the section
 * edits hang on, so it has to happen first. Copying is offered here; going
 * on without is allowed and says what that means.
 */
function ImageGate({
  kind,
  id,
  subject,
  embedded,
  onContinue,
}: {
  kind: Kind;
  id: string;
  subject: Subject;
  embedded: boolean;
  onContinue: () => void;
}) {
  const queryClient = useQueryClient();
  const paths = PATHS[kind];
  const [saveError, setSaveError] = useState<string | null>(null);
  const check = useHtmlCheck(subject.html);

  const onHtml = useCallback(
    (html: string) => {
      setSaveError(null);
      const request =
        kind === 'template'
          ? apiFetch(`/templates/${id}`, { method: 'PUT', body: { name: subject.name, html } })
          : apiFetch(`/campaigns/${id}`, { method: 'PATCH', body: { html } });
      request
        .then(() => queryClient.invalidateQueries({ queryKey: [paths.queryKey, id] }))
        .catch((err: unknown) => {
          setSaveError(
            err instanceof ApiError && err.body.message
              ? err.body.message
              : 'The images were copied, but the mail could not be saved with the new addresses. Copy them from the HTML editor instead.',
          );
        });
    },
    [kind, id, subject.name, queryClient, paths.queryKey],
  );
  const rehost = useRehostImages(subject.html, onHtml);

  return (
    <div className="mx-auto flex min-h-dvh max-w-[640px] flex-col gap-4 px-4 py-8">
      <Link to={`/${paths.page}/${id}`} className="no-underline">
        ← {paths.back}
      </Link>
      <h1 className="m-0 text-2xl font-semibold tracking-tight">Copy the images first</h1>
      <p className="m-0 text-sm text-muted">
        “{subject.name}” has images that are not on our own media domain. Copying them changes the
        HTML of the mail, and section edits are tied to that HTML. So copy the images before you
        start editing sections.
      </p>
      {check.report ? (
        <CopyImagesPanel
          images={check.report.images}
          run={rehost.run}
          onCopy={() => void rehost.start()}
          disabled={check.pending}
        />
      ) : check.isError ? (
        <Alert>The images could not be checked. Try again shortly.</Alert>
      ) : (
        <p className="m-0 text-sm text-muted" role="status">
          Checking the images…
        </p>
      )}
      {saveError !== null ? <Alert>{saveError}</Alert> : null}
      <div className="rounded-xl border border-border bg-card p-4 text-sm">
        <h2 className="m-0 text-base font-semibold">Edit without copying</h2>
        <ul className="mb-3 mt-2 flex flex-col gap-1 pl-4 text-muted">
          <li>Images stored at Mailchimp stop showing when the Mailchimp account is closed.</li>
          {embedded ? (
            <li>
              This mail has images embedded in the HTML. The server refuses to save a mail with
              embedded images, so your section edits cannot be saved until they are copied.
            </li>
          ) : null}
          <li>
            If you copy the images later, the HTML is rewritten: the section edits you made become
            part of it, and sections you hid or removed are gone for good.
          </li>
        </ul>
        <Button variant="secondary" size="sm" onClick={onContinue}>
          Edit without copying
        </Button>
      </div>
    </div>
  );
}

/**
 * The section editor for an HTML template (`/skabeloner/:id/sections`) or
 * an HTML campaign (`/kampagner/:id/sections`). The stored original, when
 * there is one, is what the sections are cut from; otherwise the HTML of
 * the mail is.
 */
export function SectionEditorPage({ kind }: { kind: Kind }) {
  const { id = '' } = useParams<{ id: string }>();
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const paths = PATHS[kind];
  const [withoutCopy, setWithoutCopy] = useState(false);
  const [reloads, setReloads] = useState(0);

  // Same cache entries as the template page and the campaign editor, same shape.
  const { data, isLoading } = useQuery({
    queryKey: [paths.queryKey, id],
    queryFn: (): Promise<TemplateDetailResponse | { campaign: Campaign }> =>
      apiFetch(`/${paths.api}/${id}`),
    enabled: id !== '',
  });
  const subject = useMemo((): Subject | undefined => {
    if (!data) return undefined;
    if ('template' in data) {
      const { template } = data;
      return {
        name: template.name,
        html: template.html,
        isHtml: template.kind === 'html',
        sections: template.sections ?? null,
        locked: null,
      };
    }
    const { campaign } = data;
    return {
      name: campaign.name,
      html: campaign.html,
      isHtml: campaign.blockJson === null && campaign.html.trim() !== '',
      sections: campaign.sections ?? null,
      locked:
        campaign.status === 'draft'
          ? null
          : 'The campaign has been approved or sent and can no longer be edited.',
    };
  }, [data]);

  const original = subject?.sections?.original ?? subject?.html ?? '';
  const doc = useMemo(() => analyseSections(original), [original]);
  const initialModel: SectionEditModel = subject?.sections?.model ?? emptySectionModel();

  const onSave = useCallback(
    async (model: SectionEditModel, originalHash: string): Promise<SectionSaveResult> => {
      try {
        const result = await apiFetch<SectionSaveResponse>(`/${paths.api}/${id}/sections`, {
          method: 'PUT',
          body: { model, originalHash },
        });
        void queryClient.invalidateQueries({ queryKey: [paths.queryKey, id] });
        void queryClient.invalidateQueries({ queryKey: [paths.api] });
        return { unmapped: result.unmapped };
      } catch (err) {
        throw sectionSaveFailure(err);
      }
    },
    [paths.api, id, queryClient],
  );

  const pageHref = `/${paths.page}/${id}`;
  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-muted" role="status">
        Loading the mail…
      </div>
    );
  }
  if (!subject) {
    return (
      <div className="mx-auto max-w-[640px] px-4 py-8">
        <Alert>The {kind} was not found.</Alert>
      </div>
    );
  }
  if (!subject.isHtml) {
    return (
      <div className="mx-auto flex max-w-[640px] flex-col gap-3 px-4 py-8">
        <Alert>
          This {kind} is built with blocks, not HTML. Sections are for imported HTML newsletters;
          edit this one in the block editor.
        </Alert>
        <Link to={pageHref}>← {paths.back}</Link>
      </div>
    );
  }

  const readOnlyReason = !isEditor
    ? `Only editors and administrators can change a ${kind}.`
    : (subject.locked ?? undefined);
  const readOnly = readOnlyReason !== undefined;
  const foreign = doc.problems.filter(
    (p) => p.code === 'image_embedded' || p.code === 'image_mailchimp_hosted',
  );
  const embedded = foreign.some((p) => p.code === 'image_embedded');

  if (!readOnly && subject.sections === null && foreign.length > 0 && !withoutCopy) {
    return (
      <ImageGate
        kind={kind}
        id={id}
        subject={subject}
        embedded={embedded}
        onContinue={() => setWithoutCopy(true)}
      />
    );
  }

  return (
    <SectionEditorScreen
      key={`${doc.sourceHash}:${reloads}`}
      title={subject.name}
      back={{ to: pageHref, label: paths.back }}
      htmlHref={pageHref}
      doc={doc}
      original={original}
      initialModel={initialModel}
      readOnly={readOnly}
      readOnlyReason={readOnlyReason}
      onSave={onSave}
      onReload={() => {
        void queryClient
          .invalidateQueries({ queryKey: [paths.queryKey, id] })
          .then(() => setReloads((n) => n + 1));
      }}
    />
  );
}
